import React, { useMemo } from "react";
import styled from "styled-components";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import Brightness2Icon from "@mui/icons-material/Brightness2";
import { DayData } from "../types";

interface DataTableProps {
  data: DayData[];
  selectedMachineType: string;
}

interface RowDef {
  machineType: string;
  machineNumber: number;
  shift: "Day" | "Night";
}

interface CellValues {
  parts: number;
  minutes: number;
}

const MACHINE_TYPE_ORDER: Record<string, number> = {
  CNC: 0,
  Edge: 1,
  Saw: 2,
};

function machineTypeSortKey(type: string): number {
  return MACHINE_TYPE_ORDER[type] ?? 99;
}

const TableWrapper = styled.div`
  margin: 24px 0;
  width: 100%;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: auto;
`;

const StyledHeading = styled.h2`
  text-align: center;
  background: #f5f5f5;
  margin: 0;
  padding: 14px 0;
  font-size: 18px;
  color: #1a1a2e;
  border-bottom: 1px solid #e0e0e0;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead tr {
    background-color: #2e282a;
    color: #fff;
    font-weight: 600;
  }

  th,
  td {
    padding: 10px 10px;
    border-bottom: 1px solid #e0e0e0;
    text-align: center;
    white-space: nowrap;
  }

  th {
    font-weight: 600;
  }
`;

const DayRow = styled.tr`
  background-color: #fff8e1;
`;

const NightRow = styled.tr`
  background-color: #1a2744;
  color: #fff;

  svg {
    fill: #fff;
  }
`;

const TotalRow = styled.tr`
  font-weight: 700;
  background-color: #f0f0f0;
`;

const PartsCell = styled.td`
  color: #fff;
  font-weight: 600;
  background-color: rgba(45, 106, 79, 0.88);
`;

const DividerCell = styled.td`
  border-right: 2px solid #2e282a;
`;

const DividerTh = styled.th`
  border-right: 2px solid #2e282a;
`;

const SunIcon = styled(WbSunnyIcon)`
  color: #fbc02d;
  font-size: 18px !important;
`;

const MoonIcon = styled(Brightness2Icon)`
  color: #7986cb;
  font-size: 18px !important;
`;

const SHIFT_ICONS: Record<string, React.ReactNode> = {
  Day: <SunIcon />,
  Night: <MoonIcon />,
};

function computeMinPerPart(minutes: number, parts: number): string {
  return parts > 0 ? (minutes / parts).toFixed(2) : "0";
}

function rowKey(rd: RowDef): string {
  return `${rd.machineType}|${rd.machineNumber}|${rd.shift}`;
}

const DataTable: React.FC<DataTableProps> = ({ data, selectedMachineType }) => {
  const allDates = useMemo(
    () => Array.from(new Set(data.map((d) => d.date))).sort(),
    [data]
  );

  const rowDefinitions = useMemo<RowDef[]>(() => {
    const seen = new Set<string>();
    data.forEach((dayData) =>
      dayData.data.forEach((row) =>
        seen.add(`${row.machineType}|${row.machineNumber}|${row.shift}`)
      )
    );
    return Array.from(seen)
      .map((key) => {
        const [machineType, machineNumberStr, shift] = key.split("|");
        return {
          machineType,
          machineNumber: parseInt(machineNumberStr, 10),
          shift: shift as "Day" | "Night",
        };
      })
      .sort(
        (a, b) =>
          machineTypeSortKey(a.machineType) - machineTypeSortKey(b.machineType) ||
          a.machineNumber - b.machineNumber ||
          (a.shift === "Day" ? -1 : 1)
      );
  }, [data]);

  const pivot = useMemo(() => {
    const map: Record<string, Record<string, CellValues>> = {};
    for (const rd of rowDefinitions) {
      map[rowKey(rd)] = {};
    }
    for (const dayData of data) {
      for (const row of dayData.data) {
        const key = `${row.machineType}|${row.machineNumber}|${row.shift}`;
        if (!map[key]) continue;
        if (!map[key][dayData.date]) {
          map[key][dayData.date] = { parts: 0, minutes: 0 };
        }
        map[key][dayData.date].parts += row.totalParts;
        map[key][dayData.date].minutes += Math.floor(row.totalSeconds / 60);
      }
    }
    return map;
  }, [data, rowDefinitions]);

  const { dateTotals, rowTotals, grandTotal } = useMemo(() => {
    const dt: Record<string, CellValues> = {};
    allDates.forEach((d) => (dt[d] = { parts: 0, minutes: 0 }));

    const rt: Record<string, CellValues> = {};
    for (const rd of rowDefinitions) {
      rt[rowKey(rd)] = { parts: 0, minutes: 0 };
    }

    const gt: CellValues = { parts: 0, minutes: 0 };

    for (const rd of rowDefinitions) {
      const key = rowKey(rd);
      for (const date of allDates) {
        const cell = pivot[key]?.[date] || { parts: 0, minutes: 0 };
        rt[key].parts += cell.parts;
        rt[key].minutes += cell.minutes;
        dt[date].parts += cell.parts;
        dt[date].minutes += cell.minutes;
      }
      gt.parts += rt[key].parts;
      gt.minutes += rt[key].minutes;
    }

    return { dateTotals: dt, rowTotals: rt, grandTotal: gt };
  }, [allDates, rowDefinitions, pivot]);

  if (!data || data.length === 0) return null;

  return (
    <TableWrapper>
      <StyledHeading>Weekly Performance {selectedMachineType}</StyledHeading>
      <StyledTable>
        <thead>
          <tr>
            <th rowSpan={2}>Machine</th>
            <th rowSpan={2}>Shift</th>
            {allDates.map((date) => (
              <th key={date} colSpan={3}>
                {date}
              </th>
            ))}
            <th colSpan={3}>TOTAL</th>
          </tr>
          <tr>
            {allDates.map((date) => (
              <React.Fragment key={`${date}-sub`}>
                <th>Parts</th>
                <th>Min</th>
                <DividerTh>Min/Part</DividerTh>
              </React.Fragment>
            ))}
            <th>Parts</th>
            <th>Min</th>
            <th>Min/Part</th>
          </tr>
        </thead>
        <tbody>
          {rowDefinitions.map((rd) => {
            const key = rowKey(rd);
            const rt = rowTotals[key];
            const Row = rd.shift === "Night" ? NightRow : DayRow;

            return (
              <Row key={key}>
                <td>{`${rd.machineType} ${rd.machineNumber}`}</td>
                <td>{SHIFT_ICONS[rd.shift] ?? rd.shift}</td>
                {allDates.map((date) => {
                  const cell = pivot[key]?.[date] || { parts: 0, minutes: 0 };
                  return (
                    <React.Fragment key={`${key}-${date}`}>
                      <PartsCell>{cell.parts}</PartsCell>
                      <td>{cell.minutes}</td>
                      <DividerCell>
                        {computeMinPerPart(cell.minutes, cell.parts)}
                      </DividerCell>
                    </React.Fragment>
                  );
                })}
                <td>{rt.parts}</td>
                <td>{rt.minutes}</td>
                <td>{computeMinPerPart(rt.minutes, rt.parts)}</td>
              </Row>
            );
          })}
          <TotalRow>
            <td colSpan={2}>TOTAL</td>
            {allDates.map((date) => {
              const dt = dateTotals[date];
              return (
                <React.Fragment key={`total-${date}`}>
                  <PartsCell>{dt.parts}</PartsCell>
                  <td>{dt.minutes}</td>
                  <DividerCell>
                    {computeMinPerPart(dt.minutes, dt.parts)}
                  </DividerCell>
                </React.Fragment>
              );
            })}
            <td>{grandTotal.parts}</td>
            <td>{grandTotal.minutes}</td>
            <td>{computeMinPerPart(grandTotal.minutes, grandTotal.parts)}</td>
          </TotalRow>
        </tbody>
      </StyledTable>
    </TableWrapper>
  );
};

export default DataTable;
